/**
 * FileUploadComponent.js
 *
 * DESCRIPTION:
 * File and folder upload component with drag&drop and click support.
 *
 * CHANGES (2026-01-05):
 * - Custom onDrop handler to preserve recursive folder structure
 * - webkitGetAsEntry API for full path preservation
 * - Hierarchical tree view with indentation
 * - Fixed: subfolder files were previously missing
 * - Fixed: files were placed at wrong level
 *
 * CHANGES (2026-01-09):
 * - Added i18n translations using useTranslation hook
 *
 * REQUIREMENTS:
 * - 2.1: Folder selection - recursive structure
 * - 2.2: Indented tree view display
 */

/* eslint-disable no-empty */
import React, { useEffect, useState, useMemo, useContext, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { AppContext } from "../AppContextProvider";
import {
  traverseDataTransferItems,
  convertToFilesWithPath,
  buildFolderStructure,
} from "../utils/folderUpload/folderTraversal";

import delete_icon from "../assets/media/icons/delete-vis.svg";
import google_logo from "../assets/media/icons/social/google.png";
import amazon_logo from "../assets/media/icons/social/amazon.png";
import instagram_logo from "../assets/media/icons/social/instagram.png";
import whatsapp_logo from "../assets/media/icons/social/whatsapp.png";
import social1_logo from "../assets/media/icons/social/social1.png";
import social2_logo from "../assets/media/icons/social/social2.png";

const Socials_logos = [amazon_logo, google_logo, instagram_logo, whatsapp_logo, social1_logo, social2_logo];

const FILE_TYPE_CONFIG = {
  image:   { color: '#2BBCD9', bg: 'rgba(43,188,217,0.15)', label: 'Image',   d: 'M21 3H3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1zM8.5 8.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zM4 19l4.5-6 3.5 4 3.5-4.5L20 19H4z' },
  video:   { color: '#E040FB', bg: 'rgba(224,64,251,0.15)', label: 'Video',   d: 'M15 12l-6 4V8l6 4zm7-7v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z' },
  audio:   { color: '#FF9800', bg: 'rgba(255,152,0,0.15)', label: 'Audio',   d: 'M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z' },
  text:    { color: '#4CAF50', bg: 'rgba(76,175,80,0.15)', label: 'Doc',     d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8 13h8v1.5H8V13zm0 3h8v1.5H8V16zm0-6h3v1.5H8V10z' },
  code:    { color: '#FFD740', bg: 'rgba(255,215,64,0.15)', label: 'Code',   d: 'M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z' },
  archive: { color: '#FF5722', bg: 'rgba(255,87,34,0.15)', label: 'Archive', d: 'M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z' },
  default: { color: '#90A4AE', bg: 'rgba(144,164,174,0.15)', label: 'File',  d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z' },
};

const CODE_EXTS = new Set(['js','jsx','ts','tsx','py','rb','java','c','cpp','h','cs','go','rs','php','html','css','scss','json','xml','yaml','yml','sh','sql','r','swift','kt','dart','vue','svelte']);
const ARCHIVE_EXTS = new Set(['zip','rar','7z','tar','gz','bz2','xz','tgz','cab','iso']);

function getFileTypeConfig(fileName) {
  const ext = extFromName(fileName);
  if (CODE_EXTS.has(ext)) return FILE_TYPE_CONFIG.code;
  if (ARCHIVE_EXTS.has(ext)) return FILE_TYPE_CONFIG.archive;
  const mime = (() => {
    if (/^(jpg|jpeg|png|gif|bmp|webp|svg|ico|tiff|avif|heic)$/i.test(ext)) return 'image';
    if (/^(mp4|avi|mkv|mov|wmv|flv|webm|m4v|3gp)$/i.test(ext)) return 'video';
    if (/^(mp3|wav|ogg|flac|aac|wma|m4a|opus)$/i.test(ext)) return 'audio';
    if (/^(pdf|doc|docx|txt|rtf|odt|xls|xlsx|csv|ppt|pptx|md)$/i.test(ext)) return 'text';
    return null;
  })();
  return FILE_TYPE_CONFIG[mime] || FILE_TYPE_CONFIG.default;
}

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  padding: "8px 16px",
  borderWidth: 1,
  borderRadius: 8,
  borderColor: "rgba(255, 255, 255, 0.3)",
  borderStyle: "dashed",
  background: "rgba(59, 65, 98, 0.4)",
  gap: "6px",
  transition: "border .24s ease-in-out",
  cursor: "pointer",
  minHeight: "85px",
};

const focusedStyle = {};
const acceptStyle = { borderColor: "#ffffff" };
const rejectStyle = { borderColor: "#ff1744" };

const uniqueSuffix = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const extFromName = (n = "") => (n.match(/\.([^.]+)$/)?.[1] || "").toLowerCase();
const stripExt = (n = "") => n.replace(/\.[^.]+$/, "");
const isGenericBase = (b = "") => /^(image|photo|picture|screenshot)(\s*\(\d+\)|[-_\s]*\d+)?$/i.test(b.trim());

function allocateSafeName(origName, type, used) {
  const base0 = stripExt(origName || "") || (type?.startsWith("image/") ? "image" : "file");
  const ext0 = extFromName(origName || "") || type?.split("/")[1] || "bin";
  const base = isGenericBase(base0) ? `${base0}-${uniqueSuffix()}` : base0;
  const ext = (ext0 || "bin").toLowerCase();
  let name = `${base}.${ext}`,
    i = 1;
  while (used.has(name)) name = `${base}-${i++}.${ext}`;
  used.add(name);
  return name;
}

function renameFilePreservingMetaAndPath(file, newName, originalPath) {
  const f2 = new File([file], newName, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified ?? Date.now(),
  });
  
  // Preserve folder structure: replace only the filename in the path
  let newPath = newName;
  if (originalPath && originalPath.includes('/')) {
    const pathParts = originalPath.split('/');
    pathParts[pathParts.length - 1] = newName;
    newPath = pathParts.join('/');
  }
  
  try {
    Object.defineProperty(f2, "path", { value: newPath, configurable: true });
  } catch {}
  try {
    Object.defineProperty(f2, "webkitRelativePath", { value: newPath, configurable: true });
  } catch {}
  return f2;
}

const isAndroid = () => /Android/i.test(navigator.userAgent);
const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

async function makePreviewUrl(file, maxSide = 480, quality = 0.8) {
  if (!file.type?.startsWith("image/")) return null;
  await new Promise((r) => setTimeout(r, 0));
  try {
    const bmp = await createImageBitmap(file);
    const w = bmp.width,
      h = bmp.height;
    const ratio = Math.min(1, maxSide / Math.max(w, h));
    const outW = Math.max(1, Math.round(w * ratio));
    const outH = Math.max(1, Math.round(h * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bmp, 0, 0, outW, outH);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    return blob ? URL.createObjectURL(blob) : null;
  } catch {
    return null;
  }
}

/**
 * FolderTreeItem - Recursive tree item display component
 * Displays folder structure with indentation
 *
 * @param {Object} node - FolderNode object
 * @param {number} depth - Indentation depth
 * @param {Function} onDelete - Delete callback
 * @param {Function} t - Translation function
 */
function FolderTreeItem({ node, depth = 0, onDelete, t }) {
  const marginLeft = depth * 16;
  const isFolder = node.type === "folder";

  if (isFolder) {
    return (
      <>
        <div
          className="folder-tree-item folder-card"
          style={{ marginLeft: `${marginLeft}px` }}
        >
          <div className="folder-card-icon" style={{ background: 'rgba(255,193,7,0.15)' }}>
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
              <path
                d="M12.5049 13H3.49508C3.09846 12.9992 2.71833 12.8411 2.43807 12.5605C2.1578 12.2799 2.00026 11.8995 2 11.5029V4.49707C2.00088 4.09979 2.1593 3.71907 2.44049 3.43842C2.72169 3.15778 3.10272 3.00011 3.5 3H6.5C6.81011 3.00026 7.11254 3.09654 7.36574 3.27559C7.61893 3.45465 7.81049 3.7077 7.91408 4H12.5088C12.9049 4.00127 13.2843 4.15958 13.5639 4.44023C13.8435 4.72088 14.0003 5.10095 14 5.49707V11.5029C13.9997 11.8995 13.8422 12.2799 13.5619 12.5605C13.2816 12.8412 12.9015 12.9992 12.5049 13Z"
                fill="#FFC107"
              />
            </svg>
          </div>
          <div className="folder-card-info">
            <span className="folder-card-name">{node.name}/</span>
            <span className="folder-card-meta">
              {node.children.length} {node.children.length === 1 ? t('common.labels.entry') : t('common.labels.entries')}
            </span>
          </div>
          <button
            type="button"
            className="file-card-remove"
            onClick={() => onDelete(node.path)}
            title={t('common.buttons.delete') || 'Delete'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        {node.children.map((child, idx) => (
          <FolderTreeItem key={`${child.path}-${idx}`} node={child} depth={depth + 1} onDelete={onDelete} t={t} />
        ))}
      </>
    );
  }

  const typeConfig = getFileTypeConfig(node.name);

  return (
    <div
      className="folder-tree-item file-card"
      style={{ marginLeft: `${marginLeft}px` }}
    >
      <div className="file-card-icon" style={{ background: typeConfig.bg }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill={typeConfig.color}>
          <path d={typeConfig.d} />
        </svg>
      </div>
      <div className="file-card-info">
        <span className="file-card-name">{node.name}</span>
        <span className="file-card-meta">{formatFileSize(node.size || 0)}</span>
      </div>
      <button
        type="button"
        className="file-card-remove"
        onClick={() => onDelete(node.path)}
        title={t('common.buttons.delete') || 'Delete'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
}

/**
 * Format file size to readable format
 */
function formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

export default function FileUploadComponent(props) {
  const { t } = useTranslation();
  const context = useContext(AppContext);
  const maxUploadSize = process.env.MAX_UPLOAD_SIZE ?? 536870912;
  const dropZoneRef = useRef(null);

  const [isRender, setIsRender] = useState(() => (props.files || []).some((k) => !k.isDelete));
  const [isFileRender, setIsFileRender] = useState(false);
  const [isFile, setIsFile] = useState(() => (props.files || []).some((k) => !k.isDelete));
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");

  // Global drag/drop prevention - prevents browser from opening files in new tabs
  // when accidentally dropped outside the dropzone
  useEffect(() => {
    const preventDefaultDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const preventDefaultDrop = (e) => {
      // Only prevent if not dropped on our dropzone
      if (!dropZoneRef.current?.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('dragover', preventDefaultDrag);
    document.addEventListener('dragenter', preventDefaultDrag);
    document.addEventListener('drop', preventDefaultDrop);

    return () => {
      document.removeEventListener('dragover', preventDefaultDrag);
      document.removeEventListener('dragenter', preventDefaultDrag);
      document.removeEventListener('drop', preventDefaultDrop);
    };
  }, []);

  /**
   * CUSTOM DROP HANDLER - Requirement 2.1
   * react-dropzone does not support recursive folder traversal,
   * so we use native event handler with webkitGetAsEntry
   */
  const handleNativeDrop = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
      setIsProcessing(true);
      setProcessingMessage(t('files.upload.analyzing'));

      try {
        const items = event.dataTransfer?.items;
        if (!items || items.length === 0) {
          setIsProcessing(false);
          return;
        }

        setProcessingMessage(t('files.upload.readingFiles'));
        const filesWithPath = await traverseDataTransferItems(items, (processed, currentPath) => {
          setProcessingMessage(`${t('files.upload.processing')}: ${processed} ${t('files.upload.files')}...`);
        });

        if (filesWithPath.length === 0) {
          setIsProcessing(false);
          return;
        }

        setProcessingMessage(`${filesWithPath.length} ${t('files.upload.filesFound')}`);
        const files = convertToFilesWithPath(filesWithPath);

        const used = new Set((props.files || []).map((f) => f.name));
        const prepared = [];
        const CHUNK_SIZE = 500;
        const MAX_PREVIEWS = 100; // Limit preview generation to first 100 images

        for (let i = 0; i < files.length; i += CHUNK_SIZE) {
          const chunk = files.slice(i, i + CHUNK_SIZE);
          
          for (let j = 0; j < chunk.length; j++) {
            const file = chunk[j];
            const globalIndex = i + j;

            // Only generate previews for first MAX_PREVIEWS images to save memory
            let preview = null;
            if (file.type?.startsWith("image/") && globalIndex < MAX_PREVIEWS) {
              if (isAndroid()) {
                preview = (await makePreviewUrl(file, 480, 0.8)) || URL.createObjectURL(file);
              } else {
                preview = URL.createObjectURL(file);
              }
            }

            try {
              Object.assign(file, { preview, isDelete: false });
            } catch {}

            prepared.push(file);
          }
          
          // Update progress and yield to browser between chunks
          setProcessingMessage(t('files.upload.fileNumber', { current: Math.min(i + CHUNK_SIZE, files.length), total: files.length }));
          if (i + CHUNK_SIZE < files.length) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }

        props.setFiles((prev) => [...prev, ...prepared]);
        setIsRender(true);
        setIsFileRender(!isFileRender);
        setIsFile(true);
      } catch (error) {
        console.error("Error processing files:", error);
      } finally {
        setIsProcessing(false);
        setProcessingMessage("");
      }
    },
    [props.files, isFileRender, t]
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleFilesFromInput = useCallback(
    async (filesArray) => {
      setIsRender(true);
      setIsFileRender(!isFileRender);
      setIsProcessing(true);
      setProcessingMessage(t('files.upload.processingFiles'));

      try {
        const used = new Set((props.files || []).map((f) => f.name));
        const prepared = [];
        const CHUNK_SIZE = 500;
        const MAX_PREVIEWS = 100;

        for (let i = 0; i < filesArray.length; i += CHUNK_SIZE) {
          const chunk = filesArray.slice(i, i + CHUNK_SIZE);
          
          for (let j = 0; j < chunk.length; j++) {
            const file = chunk[j];
            const globalIndex = i + j;

            const originalPath = file.path || file.webkitRelativePath || file.name;
            const safeName = allocateSafeName(file.name, file.type, used);
            const finalFile =
              safeName === file.name
                ? (() => {
                    try {
                      if (!file.path) {
                        Object.defineProperty(file, "path", { value: originalPath, configurable: true });
                      }
                    } catch {}
                    return file;
                  })()
                : renameFilePreservingMetaAndPath(file, safeName, originalPath);

            // Only generate previews for first MAX_PREVIEWS images
            let preview = null;
            if (finalFile.type?.startsWith("image/") && globalIndex < MAX_PREVIEWS) {
              if (isAndroid()) {
                preview = (await makePreviewUrl(finalFile, 480, 0.8)) || URL.createObjectURL(finalFile);
              } else {
                preview = URL.createObjectURL(finalFile);
              }
            }

            try {
              Object.assign(finalFile, { preview, isDelete: false });
            } catch {}

            prepared.push(finalFile);
          }
          
          // Update progress and yield between chunks
          setProcessingMessage(t('files.upload.fileNumber', { current: Math.min(i + CHUNK_SIZE, filesArray.length), total: filesArray.length }));
          if (i + CHUNK_SIZE < filesArray.length) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }

        props.setFiles((prev) => [...prev, ...prepared]);
        setIsFile(true);
      } catch (error) {
        console.error("Error processing files:", error);
      } finally {
        setIsProcessing(false);
        setProcessingMessage("");
      }
    },
    [props.files, isFileRender, t]
  );

  const handleSelectFolder = useCallback(async () => {
    const supportsFileSystemAccess = 
      typeof window !== 'undefined' && 
      'showDirectoryPicker' in window;

    if (supportsFileSystemAccess) {
      try {
        const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
        const allFiles = [];
        let fileCount = 0;
        const MAX_UI_FILES = 50000; // Maximum files to load into UI
        const BATCH_SIZE = 500;
        
        setIsProcessing(true);
        setProcessingMessage(t('files.upload.scanning'));
        
        async function* getFilesRecursively(handle, path = '') {
          for await (const entry of handle.values()) {
            const entryPath = path ? `${path}/${entry.name}` : entry.name;
            if (entry.kind === 'file') {
              // Don't create new File objects - just get the original and add path
              const originalFile = await entry.getFile();
              try {
                Object.defineProperty(originalFile, 'path', { value: entryPath, writable: false, configurable: true });
                Object.defineProperty(originalFile, 'webkitRelativePath', { value: entryPath, writable: false, configurable: true });
              } catch {
                // If defineProperty fails, create minimal wrapper
                originalFile._path = entryPath;
              }
              yield originalFile;
            } else if (entry.kind === 'directory') {
              yield* getFilesRecursively(entry, entryPath);
            }
          }
        }

        for await (const file of getFilesRecursively(dirHandle, dirHandle.name)) {
          allFiles.push(file);
          fileCount++;
          
          // Update progress every batch
          if (fileCount % BATCH_SIZE === 0) {
            setProcessingMessage(`${t('files.upload.scanning')}: ${fileCount} ${t('common.labels.files')}`);
            // Yield to browser
            await new Promise(resolve => setTimeout(resolve, 0));
          }
          
          // Stop if we hit the limit
          if (fileCount >= MAX_UI_FILES) {
            console.warn(`[FileUploadComponent] Reached UI limit of ${MAX_UI_FILES} files`);
            setProcessingMessage(t('files.upload.tooManyFiles', { max: MAX_UI_FILES }));
            break;
          }
        }

        setIsProcessing(false);
        setProcessingMessage("");
        
        if (allFiles.length > 0) {
          handleFilesFromInput(allFiles);
        }
      } catch (err) {
        setIsProcessing(false);
        setProcessingMessage("");
        if (err.name !== 'AbortError') {
          console.error('Folder picker error:', err);
        }
      }
    } else {
      document.getElementById('folder-input')?.click();
    }
  }, [handleFilesFromInput, t]);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone(
    props.dataimport
      ? {
          accept: {
            "application/zip": [".zip"],
            "application/x-zip-compressed": [".zip"],
            "application/x-zip": [".zip"],
            "application/octet-stream": [".zip"],
          },
          multiple: false,
          noDrag: false,
          onDrop: (acceptedFiles, rejectedFiles) => {
            console.log("[DataImport] onDrop called:", { acceptedFiles, rejectedFiles });
            if (acceptedFiles.length === 0) {
              console.warn("[DataImport] No files accepted. Check file type.");
              return;
            }
            setIsRender(true);
            setIsFileRender(!isFileRender);
            const imageFiles = [...props.files];
            acceptedFiles.forEach((file) =>
              imageFiles.push(
                Object.assign(file, {
                  preview: URL.createObjectURL(file),
                })
              )
            );
            props.setFiles(imageFiles);
            setIsFile(true);
          },
        }
      : {
          noClick: false,
          noDrag: true,
          multiple: true,
          useFsAccessApi: false,
          onDrop: async (acceptedFiles) => {
            setIsRender(true);
            setIsFileRender(!isFileRender);

            const used = new Set((props.files || []).map((f) => f.name));
            const prepared = [];

            for (const file of acceptedFiles) {
              const originalPath = file.path || file.webkitRelativePath || file.name;
              const safeName = allocateSafeName(file.name, file.type, used);
              const finalFile =
                safeName === file.name
                  ? (() => {
                      try {
                        Object.defineProperty(file, "path", { value: originalPath, configurable: true });
                      } catch {}
                      return file;
                    })()
                  : renameFilePreservingMetaAndPath(file, safeName, originalPath);

              let preview;
              if (isAndroid()) {
                preview = (await makePreviewUrl(finalFile, 480, 0.8)) || URL.createObjectURL(finalFile);
              } else {
                preview = URL.createObjectURL(finalFile);
              }

              try {
                Object.assign(finalFile, { preview });
              } catch {}

              prepared.push(finalFile);
            }

            props.setFiles((prev) => [...prev, ...prepared]);
            setIsFile(true);
          },
        }
  );

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept || isDragging ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(isDragging ? { borderColor: "#2BBCD9", borderWidth: 2 } : {}),
    }),
    [isFocused, isDragAccept, isDragReject, isDragging]
  );

  useEffect(() => {
    return () => props.files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [props.files]);

  useEffect(() => {
    const has = (props.files || []).some((k) => !k.isDelete);
    props.setIsFiles(Boolean(has));
    setIsFile(Boolean(has));
    if (has) {
      setIsRender(true);
    }
  }, [props.files]);

  const deleteFileByPath = useCallback(
    (path) => {
      if (!props.files || !path) return;
      setIsRender(false);
      const updatedFiles = props.files.map((f) => {
        if (!f) return f;
        const filePath = f.path || f.name || "";
        if (filePath === path) {
          return { ...f, isDelete: true };
        }
        return f;
      });
      props.setFiles(updatedFiles);
      setTimeout(() => {
        setIsRender(true);
        setIsFileRender((prev) => !prev);
      }, 100);
    },
    [props.files]
  );

  const deleteFolderByPath = useCallback(
    (folderPath) => {
      if (!props.files || !folderPath) return;
      setIsRender(false);
      const updatedFiles = props.files.map((f) => {
        if (!f) return f;
        const filePath = f.path || f.name || "";
        if (filePath.startsWith(folderPath + "/") || filePath === folderPath) {
          return { ...f, isDelete: true };
        }
        return f;
      });
      props.setFiles(updatedFiles);
      setTimeout(() => {
        setIsRender(true);
        setIsFileRender((prev) => !prev);
      }, 100);
    },
    [props.files]
  );

  const activeFiles = useMemo(() => (props.files || []).filter((k) => !k.isDelete), [props.files]);

  const folderStructure = useMemo(() => {
    if (activeFiles.length === 0) return null;
    return buildFolderStructure(activeFiles);
  }, [activeFiles]);

  const totalSize = useMemo(() => activeFiles.reduce((sum, f) => sum + (f.size || 0), 0), [activeFiles]);

  return (
    <>
      {isProcessing && (
        <div
          style={{
            padding: "16px",
            background: "var(--background-secondary)",
            borderRadius: "8px",
            marginBottom: "12px",
            textAlign: "center",
          }}
        >
          <div style={{ marginBottom: "8px", color: "var(--text-primary)" }}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              style={{ animation: "spin 1s linear infinite", marginRight: "8px" }}
            >
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="#2BBCD9" strokeWidth="2" fill="none" strokeLinecap="round" />
            </svg>
          </div>
          <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "14px" }}>{processingMessage}</p>
        </div>
      )}

      {activeFiles.length > 0 && (
        <div className="upload-file-list-header">
          <div className="upload-file-list-header-left">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2BBCD9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/>
            </svg>
            <span className="upload-file-list-count">
              {activeFiles.length} {t('common.labels.files')}
            </span>
          </div>
          <span className="upload-file-list-size">{formatFileSize(totalSize)}</span>
        </div>
      )}

      {activeFiles.length > 0 && folderStructure && (
        <div className="upload-file-list-container">
          {isRender &&
            folderStructure.children.map((child, idx) => (
              <FolderTreeItem
                key={`${child.path}-${idx}`}
                node={child}
                depth={0}
                onDelete={child.type === "folder" ? deleteFolderByPath : deleteFileByPath}
                t={t}
              />
            ))}
        </div>
      )}

      {(!props.dataimport || activeFiles.length === 0) && (
        props.dataimport ? (
          <div ref={dropZoneRef} {...getRootProps({ style })}>
            <input {...getInputProps()} />
            <span className="modal-text">
              {t('files.upload.dropFilesHere')}
            </span>
          </div>
        ) : (
          <div
            ref={dropZoneRef}
            style={style}
            onDrop={handleNativeDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDragEnter={handleDragOver}
          >
            <input 
              type="file" 
              multiple 
              style={{ display: 'none' }} 
              id="file-input"
              onChange={(e) => {
                if (e.target.files?.length) {
                  const filesArray = Array.from(e.target.files);
                  handleFilesFromInput(filesArray);
                  e.target.value = '';
                }
              }}
            />
            <input 
              type="file" 
              webkitdirectory="" 
              directory="" 
              style={{ display: 'none' }} 
              id="folder-input"
              onChange={(e) => {
                if (e.target.files?.length) {
                  const filesArray = Array.from(e.target.files);
                  handleFilesFromInput(filesArray);
                  e.target.value = '';
                }
              }}
            />
            {isDragging ? (
              <div style={{ textAlign: 'center', width: '100%', padding: activeFiles.length > 0 ? '16px' : '30px' }}>
                <svg width={activeFiles.length > 0 ? "32" : "64"} height={activeFiles.length > 0 ? "32" : "64"} viewBox="0 0 24 24" fill="none" stroke="#2BBCD9" strokeWidth="1.5" style={{ marginBottom: '8px' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                </svg>
                <p className="modal-text" style={{ color: "#2BBCD9", fontSize: activeFiles.length > 0 ? '14px' : '18px', fontWeight: 500, margin: 0 }}>
                  {t('files.upload.dropToUpload')}
                </p>
              </div>
            ) : activeFiles.length > 0 ? (
              /* Resume mode: hide "Add more files" section */
              !props.isResumeMode ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', width: '100%', padding: '12px 16px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.5 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                    </svg>
                    <span className="modal-text" style={{ fontSize: '13px', margin: 0 }}>
                      {t('files.upload.addMoreFiles')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={handleSelectFolder}
                      style={{ 
                        padding: '6px 14px', 
                        fontSize: '12px',
                        color: '#ffffff',
                        background: 'linear-gradient(135deg, #A83279 0%, #8B2A65 100%)',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      {t('common.labels.folder')}
                    </button>
                    <button
                      type="button"
                      onClick={() => document.getElementById('file-input')?.click()}
                      style={{ 
                        padding: '6px 14px', 
                        fontSize: '12px',
                        color: '#ffffff',
                        background: 'linear-gradient(135deg, #A83279 0%, #8B2A65 100%)',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      {t('common.labels.files')}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '12px 16px' }}>
                  <span className="modal-text" style={{ fontSize: '13px', color: '#8b9eb0', fontStyle: 'italic' }}>
                    {t('files.upload.resumeModeNoAdd') || 'Resume mode - file selection locked'}
                  </span>
                </div>
              )
            ) : (
              <div style={{ textAlign: 'center', width: '100%', padding: '24px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 1 }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                  </svg>
                </div>
                <p className="modal-text" style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: 500 }}>
                  {t('files.upload.dragAndDropFolders')}
                </p>
                <p className="modal-text" style={{ margin: '0 0 20px 0', fontSize: '14px', opacity: 1 }}>
                  {t('files.upload.dragAndDrop')}
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleSelectFolder}
                    style={{ 
                      padding: '10px 24px', 
                      fontSize: '14px', 
                      color: '#ffffff',
                      background: 'linear-gradient(135deg, #A83279 0%, #8B2A65 100%)',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    {t('files.upload.selectFolder')}
                  </button>
                  <button
                    type="button"
                    onClick={() => document.getElementById('file-input')?.click()}
                    style={{ 
                      padding: '10px 24px', 
                      fontSize: '14px', 
                      color: '#ffffff',
                      background: 'linear-gradient(135deg, #A83279 0%, #8B2A65 100%)',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    {t('common.labels.files')}
                  </button>
                </div>
                <p style={{ margin: '16px 0 0 0', fontSize: '11px', opacity: 1, color: '#fff' }}>
                  {t('files.upload.dragDropTip')}
                </p>
              </div>
            )}
          </div>
        )
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
